/**
 * Host-based routing helpers.
 *
 * Deployment plan (production):
 *   - lodge.squareandcompass.store  → main lodge management app
 *   - admin.squareandcompass.store  → super-admin portal
 *
 * The app decides which set of routes to render based on `window.location.hostname`.
 *
 * Rules:
 *   - Any hostname whose first DNS label is exactly "admin" → admin portal.
 *   - Any other hostname → main lodge app (this includes lodge.*, the apex, and the preview URL).
 *
 * When we need to "flip" between hosts (e.g., redirect a super-admin from the main host to
 * the admin portal), we swap the leading label:
 *
 *   lodge.squareandcompass.store  ⇄  admin.squareandcompass.store
 *
 * For the apex (`squareandcompass.store`) or any other single-label / preview host, we
 * prepend `admin.` / `lodge.` as appropriate.
 */

const ADMIN_LABEL = "admin";
const LODGE_LABEL = "lodge";

function firstLabel(hostname) {
  return (hostname || "").toLowerCase().split(".")[0];
}

function stripLeadingLabel(hostname, label) {
  const lower = (hostname || "").toLowerCase();
  const prefix = `${label}.`;
  return lower.startsWith(prefix) ? hostname.slice(prefix.length) : hostname;
}

export function isAdminHost() {
  if (typeof window === "undefined") return false;
  return firstLabel(window.location.hostname) === ADMIN_LABEL;
}

export function isLodgeHost() {
  if (typeof window === "undefined") return false;
  return firstLabel(window.location.hostname) === LODGE_LABEL;
}

function buildUrl(hostname) {
  const { protocol, port, pathname, search, hash } = window.location;
  const portPart = port ? `:${port}` : "";
  return `${protocol}//${hostname}${portPart}${pathname}${search}${hash}`;
}

/**
 * URL of the main lodge app (lodge.*). Called when we need to redirect away from admin.
 */
export function getMainHostUrl() {
  if (typeof window === "undefined") return "/";
  const hostname = window.location.hostname;
  const first = firstLabel(hostname);
  if (first === LODGE_LABEL) return buildUrl(hostname);
  if (first === ADMIN_LABEL) {
    const base = stripLeadingLabel(hostname, ADMIN_LABEL);
    return buildUrl(`${LODGE_LABEL}.${base}`);
  }
  // Preview / apex / unknown — prepend lodge.
  return buildUrl(`${LODGE_LABEL}.${hostname}`);
}

/**
 * URL of the super-admin portal (admin.*). Called when we need to redirect a superadmin
 * away from the main host.
 */
export function getAdminHostUrl() {
  if (typeof window === "undefined") return "/";
  const hostname = window.location.hostname;
  const first = firstLabel(hostname);
  if (first === ADMIN_LABEL) return buildUrl(hostname);
  if (first === LODGE_LABEL) {
    const base = stripLeadingLabel(hostname, LODGE_LABEL);
    return buildUrl(`${ADMIN_LABEL}.${base}`);
  }
  // Preview / apex / unknown — prepend admin.
  return buildUrl(`${ADMIN_LABEL}.${hostname}`);
}
